import React, { useState, useEffect, useRef } from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import useMobileDetection from "@klickinc/kfs-components-use-mobile-detection";
import GTM from "@utils/GTM";
// import Text from '@components/Text';

/**
 * Helper function to handle newlines in text content
 * @param {string} text - The text content that may contain newlines
 * @returns {React.ReactElement|string} - Text with newlines converted to BR elements
 */
const handleNewlines = (text) => {
	if (typeof text === 'string' && text.includes('\n')) {
		return text.split('\n').map((segment, i, array) => (
			<React.Fragment key={`text-nl-${i}`}>
				{segment}
				{i < array.length - 1 && <br />}
			</React.Fragment>
		));
	}
	return text;
};

const IsiComponent = ({ isi, className = '' }) => {
	const [isClient, setIsClient] = useState(false);
	const [isFixed, setIsFixed] = useState(false);
	const [isFullView, setIsFullView] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [isPartialView, setIsPartialView] = useState(true);
	const [headerHeight, setHeaderHeight] = useState(0);
	const isiRef = useRef(null);
	const contentRef = useRef(null);

	const openIsiIcon = <svg xmlns="http://www.w3.org/2000/svg" width="18" height="12" viewBox="0 0 18 12" fill="none">
		<path d="M1.92871 8.5L8.99978 1.42893L16.0708 8.5" stroke="white" stroke-width="2" stroke-linecap="round" />
		<path d="M1.92871 8.5L8.99978 1.42893L16.0708 8.5" stroke="#7AC593" stroke-opacity="0.7" stroke-width="2" stroke-linecap="round" />
	</svg>;

	useEffect(() => {
		setIsClient(true);
	}, []);

	// Effect to handle body scroll locking when ISI drawer is open
	useEffect(() => {
		if (!isClient) return;

		if (isFullView) {
			// Lock body scroll when drawer is open
			document.body.style.overflow = "hidden";
		} else {
			// Restore body scroll when drawer is closed
			document.body.style.overflow = "";
		}

		// Cleanup function to ensure scroll is restored when component unmounts
		return () => {
			document.body.style.overflow = "";
		};
	}, [isClient, isFullView]);

	useEffect(() => {
		if (!isClient) return;

		const handleScroll = () => {
			if (!isiRef.current) return;

			const isiRect = isiRef.current.getBoundingClientRect();
			const windowHeight = window.innerHeight;

			// Check if ISI is completely out of the viewport (below the visible area)
			const isOutOfViewport = isiRect.top > windowHeight;

			setIsFixed(isOutOfViewport);
		};

		window.addEventListener('scroll', handleScroll);
		// Trigger once to check initial position
		handleScroll();

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [isClient]);

	const toggleFullView = () => {
		if (isFullView) {
			// Collapsing
			setIsCollapsed(true);
			setIsFullView(false);
			setIsPartialView(false);
		} else {
			// First time expanding from initial state
			setIsFullView(true);
			setIsPartialView(false);
		}
	};

	const toggleDown = () => {
		setIsPartialView(false);
		setIsFullView(false);
		setIsCollapsed(true);
	}

	useEffect(() => {
		if (!isClient) return;

		const updateHeaderHeight = () => {
			const headerWrapper = document.querySelector('.header-wrapper');
			if (headerWrapper) {
				setHeaderHeight(headerWrapper.offsetHeight);
			}
		};

		updateHeaderHeight();
		window.addEventListener('resize', updateHeaderHeight);

		return () => {
			window.removeEventListener('resize', updateHeaderHeight);
		};
	}, [isClient]);

	const getFullHeight = () => {
		if (typeof window !== 'undefined' && window.innerWidth < 1024) {
			const windowHeight = window.innerHeight;
			const bottomSpacing = 20;
			return `${windowHeight - headerHeight - bottomSpacing}px`;
		}
		return '80vh';
	};

	const getMaxHeight = () => {
		if (isFullView) {
			GTM.isiClick('Expand', 'Show More ISI');
			return getFullHeight();
		} else if (isCollapsed) {
			GTM.isiClick('Collapse', 'Show Less ISI');
			return '50px';
		} else if (isPartialView) {
			// Check screen width directly using window object
			if (typeof window !== 'undefined' && window.innerWidth < 1024) {
				return '300px'; // Mobile size
			}
			return '190px'; // Desktop size
		} else {
			return '50px'; // Initial height
		}
	};

	const options = {
		renderText: (text) => {
			return handleNewlines(text);
		},
		renderNode: {
			'heading-3': (node, children) => (
				<h3 className="mt-4 lg:mt-6 mb-2 text-dark_green text-p_mobile lg:text-p !font-bold uppercase text-[#000000B2]">{children}</h3>
			),
			'unordered-list': (node, children) => (
				<ul className="marker:text-light_green list-disc pl-5">
					{children.map((child, index) => (
						<li key={`ul-${index}`} className="text-16 font-normal leading-[22px] mb-2 text-[#000000B2]">{child}</li>
					))}
				</ul>
			),
			'ordered-list': (node, children) => (
				<ol className="marker:text-light_green list-decimal pl-5">
					{children.map((child, index) => (
						<li key={`ol-${index}`} className="text-16 font-normal leading-[22px] mb-2 text-[#000000B2]">{child}</li>
					))}
				</ol>
			),
			'hyperlink': (node, children) => (
				node.data.uri.indexOf('pdf') > -1 ? (
					<a href={node.data.uri} target="_blank" className="underline">{children}</a>
				) : (
						<a href={node.data.uri} className="underline text-[#000000B2]">{children}</a>
					)
			),
			'paragraph': (node, children) => {
				// Check if this is an empty paragraph (no content or only empty string content)
				const isEmpty = !node.content ||
					node.content.length === 0 ||
					(node.content.length === 1 &&
						node.content[0].nodeType === 'text' &&
						(!node.content[0].value || node.content[0].value.trim() === ''));

				// For empty paragraphs, return a BR element to create visual spacing
				if (isEmpty) {
					return <br key={`empty-paragraph-${Math.random()}`} />;
				}

				// For non-empty paragraphs, return the regular paragraph
				return (
					<p className="text-p_mobile lg:text-p font-normal leading-tight text-[#000000B2]">{children}</p>
				);
			},
		},
	};

	const rawText = isi?.isi?.text?.raw;
	const rawTextIndication = isi?.indication?.text?.raw;
	const rawTextImportantSafetyInformation = isi?.importantSafetyInformation?.text?.raw;

	return (
		<>
			{/* CSS for sticky ISI */}
			<style>
				{`
					@keyframes slideInFromBottom {
						0% { transform: translateY(100%); }
						100% { transform: translateY(0); }
					}
					.sticky-isi {
						position: fixed;
						bottom: 0;
						left: 0;
						width: 100%;
						z-index: 50;
						animation: slideInFromBottom 0.3s ease-out forwards;
						max-height: ${getMaxHeight()};
						transition: max-height 0.5s ease;
						display: flex;
						flex-direction: column;
					}
					.sticky-isi-content {
						flex: 1;
						overflow-y: auto;
						-webkit-overflow-scrolling: touch;
					}
					.icon-rotate {
						transform: rotate(180deg);
						transition: transform 0.3s ease;
					}
					.icon-normal {
						transform: rotate(0deg);
						transition: transform 0.3s ease;
					}

					h6{
					color: #165F57;
					}
				`}
			</style>

			{/* Original ISI in document flow */}
			<div ref={isiRef} className={`isi py-[40px] lg:py-[118px] bg-white rounded-all_48 ${className}`}>
				<div ref={contentRef} className=" mx-auto px-[35px] xl:px-0 lg:max-w-lg xl:max-w-default">
					{isClient && rawText && documentToReactComponents(JSON.parse(rawText), options)}
				</div>
			</div>

			{/* Sticky ISI that appears when original scrolls out of view */}
			{isFixed && (
				<div className="sticky-isi">
					{/* Fixed header */}
					<div className="sticky-isi-header w-full bg-dark_green flex-shrink-0 rounded-t-[48px]">
						<div className="flex justify-between items-center py-4 sticky top-0 mx-auto max-w-default px-[25px] xl:px-0">
							{isPartialView && (
								<h3 className="text-white font-bold uppercase lg:max-w-[500px] xl:max-w-[650px] w-full lg:block hidden">Indication</h3>
							)}

							<h3 className="text-white font-bold uppercase flex-1 mobileTabletMax:text-p_medium "><span class="inline-block whitespace-pre md:hidden">indication & </span>important safety information</h3>

							<div className="flex flex-row items-center ">
								<button
									onClick={toggleFullView}
									className="lg:px-3 lg:py-1 px-2 py-1 bg-dark_green text-white  text-sm"
								>
									<div className={isFullView ? 'icon-rotate' : 'icon-normal'}>
										{openIsiIcon}
									</div>
								</button>
								{isPartialView && (
									<button className="border-l-[1px] border-[#6494A199] lg:px-3 lg:py-1 px-2 py-1 bg-dark_green text-white  text-sm" onClick={toggleDown}>
										<div className={isFullView ? 'icon-normal' : 'icon-rotate'} >
											{openIsiIcon}
										</div>
									</button>
								)}
							</div>
						</div>
					</div>

					{/* Scrollable content */}
					<div className="sticky-isi-content bg-white">
						{isPartialView && (
							<div className="lg:flex bg-white max-w-default mx-auto justify-between  flex-row gap-2 lg:pr-[90px] lg:h-[115px] overflow-hidden">
								<div className=" pt-3 bg-white pl-[20px] xl:pl-0 lg:pr-[20px] xl:pr-[35px] w-full lg:max-w-[510px] xl:max-w-[640px] ">
									<h3 className="text-dark_green text-p_mobile pb-2 lg:hidden !font-bold uppercase text-[#000000B2]">Indication</h3>
									{isClient && rawTextIndication && documentToReactComponents(JSON.parse(rawTextIndication), options)}
								</div>
								<div className=" pt-3 bg-white pl-[20px] lg:pr-0 lg:pl-[10px] xl:px-0 flex-1">
									<h3 className="text-dark_green text-p_mobile pb-2 lg:hidden !font-bold uppercase text-[#000000B2]">Important Safety Information</h3>
									{isClient && rawTextImportantSafetyInformation && documentToReactComponents(JSON.parse(rawTextImportantSafetyInformation), options)}
								</div>
							</div>
						)}

						<div className={`${isPartialView ? 'block lg:hidden' : 'block'} py-0 bg-white max-w-default mx-auto px-[25px] xl:px-0`}>
							{isClient && rawText && documentToReactComponents(JSON.parse(rawText), options)}
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default IsiComponent;